/**
 * «HVEM ER MED?» — medlemmene av organisasjonen som er med i en årgang (QA13 punkt 32, trinn 1c).
 *
 * Atle: «Når man lager en festival eller årgang, velger man hvilke personer fra organisasjonen som
 * skal med.» Til nå regnet Personer-lista ALLE medlemmer inn i hver årgang uten å lagre noe. Nå er
 * et medlem med fordi noen valgte det, og valget er en deltakelsesrad med merkelappen `member`.
 *
 * TRE STEDER VALGET GJØRES, ÉN FORM PÅ SVARET:
 *   · «Ny festival»   → `GET /v1/member-choices` (ingen forslag; festivalen finnes ikke ennå)
 *   · «Ny årgang»     → `GET /v1/festivals/:id/member-choices` (forslaget: forrige årgang)
 *   · «Legg til person» på Personer → `GET /v1/editions/:id/member-choices` (`inEdition` sier hvem
 *     som alt er med)
 *
 * Lista er hele organisasjonen og ikke en side: «Velg alle» må bety alle, og et hus har et tosifret
 * eller lavt tresifret antall medlemmer. Taket under er der så et feilkall ikke kan be om en
 * million rader.
 */
import { z } from 'zod';

import { orgRoleSchema } from '../enums.js';

/** Flest personer ett valg kan ta med. Høyere enn noe hus vi kjenner, lavere enn en feil. */
export const MEMBER_PICK_MAX = 1000;

/**
 * Personene som skal med, som personens id (ikke kontaktens): det er `person_id` som er nøkkelen
 * for «samme menneske» (kontrakten for trinn 1, §0). Dubletter tåles og telles én gang.
 */
export const memberPersonIdsSchema = z.array(z.uuid()).max(MEMBER_PICK_MAX);

export const memberChoiceDtoSchema = z
  .object({
    personId: z.uuid(),
    contactId: z.uuid(),
    displayName: z.string(),
    /** Tittelen i organisasjonen (`org_memberships.job_title`). */
    jobTitle: z.string().nullable(),
    role: orgRoleSchema,
    /** Foreslått: med i forrige årgang (som medlem eller stab), eller valgt da festivalen ble laget. */
    proposed: z.boolean(),
    /** Står alt i årgangen. Bare sann når spørsmålet gjelder en årgang som finnes. */
    inEdition: z.boolean(),
  })
  .meta({ id: 'MemberChoice' });

/**
 * Hvor forslaget kommer fra, så dialogen kan si det i én linje over lista:
 *   · `edition`  — forrige årgang av festivalen (`editionName` er navnet)
 *   · `festival` — valget som ble gjort i «Ny festival», for festivalens første årgang
 *   · `none`     — ingenting er foreslått
 */
export const memberProposalSourceSchema = z.enum(['edition', 'festival', 'none']);

export const memberChoicesResponseSchema = z
  .object({
    items: z.array(memberChoiceDtoSchema),
    proposedFrom: memberProposalSourceSchema,
    editionName: z.string().nullable(),
  })
  .meta({ id: 'MemberChoicesResponse' });

/**
 * «Legg til person» med medlemmer som ikke er med — flere på en gang (punkt 32, tredje vedtak).
 *
 * Avdelinger og roller er de samme som dialogen ellers gir den ene personen, og gjelder alle.
 */
export const addEditionMembersRequestSchema = z
  .object({
    personIds: memberPersonIdsSchema.min(1),
    departmentIds: z.array(z.uuid()).max(20).default([]),
    participationRoleIds: z.array(z.uuid()).max(20).default([]),
  })
  .meta({ id: 'AddEditionMembersRequest' });

export const addEditionMembersResponseSchema = z
  .object({
    /** Nye deltakelsesrader. */
    added: z.number().int().nonnegative(),
    /** Sto alt i årgangen; de fikk merkelappen `member` om de manglet den, og ellers ingenting. */
    alreadyIn: z.number().int().nonnegative(),
  })
  .meta({ id: 'AddEditionMembersResponse' });

export type MemberChoiceDto = z.infer<typeof memberChoiceDtoSchema>;
export type MemberProposalSource = z.infer<typeof memberProposalSourceSchema>;
export type MemberChoicesResponse = z.infer<typeof memberChoicesResponseSchema>;
export type AddEditionMembersRequest = z.infer<typeof addEditionMembersRequestSchema>;
export type AddEditionMembersResponse = z.infer<typeof addEditionMembersResponseSchema>;
